import React from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' },
];

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const map = {
    scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${map[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  );
}

export default function SuperadminWebinarsIndex({ webinars = [], filters = {}, counts = {} }) {
  const active = filters.scope || 'upcoming';

  const setScope = (key) => {
    router.get('/superadmin/webinars', key === 'upcoming' ? {} : { scope: key }, {
      preserveState: true,
      preserveScroll: true,
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Live Webinars</h1>
            <p className="mt-1 text-sm text-gray-500">Schedule webinars, manage registrations, and award CE credit.</p>
          </div>
          <Link
            href="/superadmin/webinars/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New webinar
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {TABS.map((t) => {
              const isActive = active === t.key;
              const count = counts[t.key] ?? 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setScope(t.key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border transition cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                  <span className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 text-xs rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {webinars.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No webinars in this view.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  <tr>
                    <th className="py-2 pr-3">Title</th>
                    <th className="py-2 pr-3">Instructor</th>
                    <th className="py-2 pr-3">Starts</th>
                    <th className="py-2 pr-3">CE hrs</th>
                    <th className="py-2 pr-3">Registered</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {webinars.map((w) => (
                    <tr key={w.id} className="hover:bg-gray-50">
                      <td className="py-3 pr-3 font-medium text-gray-900">
                        <Link href={`/superadmin/webinars/${w.id}`} className="hover:text-indigo-700">{w.title}</Link>
                      </td>
                      <td className="py-3 pr-3 text-gray-600">{w.instructor || '—'}</td>
                      <td className="py-3 pr-3 text-gray-600">{fmt(w.starts_at)}</td>
                      <td className="py-3 pr-3 text-gray-600 tabular-nums">{w.credit_hours}</td>
                      <td className="py-3 pr-3 text-gray-600 tabular-nums">
                        {w.registered_count}{w.capacity != null ? ` / ${w.capacity}` : ''}
                      </td>
                      <td className="py-3 pr-3"><StatusBadge status={w.status} /></td>
                      <td className="py-3 pr-3 text-right whitespace-nowrap">
                        <Link href={`/superadmin/webinars/${w.id}`} className="text-sm font-semibold text-indigo-600 hover:text-indigo-700">View</Link>
                        <span className="mx-2 text-gray-300">·</span>
                        <Link href={`/superadmin/webinars/${w.id}/edit`} className="text-sm font-semibold text-indigo-600 hover:text-indigo-700">Edit</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
